import React, { useState, useEffect } from 'react';
import { Settings, Plus, Clock, FileText, Zap, Edit2, Trash2, ToggleLeft, ToggleRight, X, Save } from 'lucide-react';
import { supabase } from '../supabaseClient';

const AutomationsTab = () => {
  const [activeSubTab, setActiveSubTab] = useState('rules'); 
  
  // --- RULES STATE ---
  const [rules, setRules] = useState([]);
  const [isRulesLoading, setIsRulesLoading] = useState(true);

  // --- TEMPLATES STATE ---
  const [templates, setTemplates] = useState([]);
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(true);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', content: '', type: 'Market Update' });

  // --- FETCH DATA ON LOAD ---
  useEffect(() => {
      fetchRules();
      fetchTemplates();
  }, []);

  const fetchRules = async () => {
      try {
          setIsRulesLoading(true);
          const { data, error } = await supabase
              .from('community_automation_rules')
              .select('*')
              .order('created_at', { ascending: false });

          if (error) throw error;

          if (data) {
              const liveRules = data.map(r => ({
                  id: r.id,
                  name: r.rule_name,
                  trigger: r.trigger_event,
                  template: r.template_id,
                  active: r.is_active
              }));
              setRules(liveRules);
          }
      } catch (err) {
          console.error("Failed to load rules:", err.message);
      } finally {
          setIsRulesLoading(false);
      }
  };

  const fetchTemplates = async () => {
      try {
          setIsTemplatesLoading(true);
          const { data, error } = await supabase
              .from('community_message_templates')
              .select('*')
              .order('created_at', { ascending: false });

          if (error) throw error;
          if (data) setTemplates(data);
      } catch (err) {
          console.error("Failed to load templates:", err.message);
      } finally {
          setIsTemplatesLoading(false);
      }
  };

  // --- RULES HANDLERS ---
  const toggleRuleStatus = async (id, currentStatus) => {
      try {
          const newStatus = !currentStatus;
          setRules(rules.map(r => r.id === id ? { ...r, active: newStatus } : r));

          const { error } = await supabase.from('community_automation_rules').update({ is_active: newStatus }).eq('id', id);
          if (error) throw error;
      } catch (err) {
          console.error("Failed to toggle rule:", err.message);
          setRules(rules.map(r => r.id === id ? { ...r, active: currentStatus } : r)); // Revert on fail
          alert("Failed to update automation status.");
      }
  };

  const deleteRule = async (id) => {
      if(!window.confirm("Are you sure you want to permanently delete this automation rule?")) return;
      try {
          const { error } = await supabase.from('community_automation_rules').delete().eq('id', id);
          if (error) throw error;
          setRules(rules.filter(r => r.id !== id));
      } catch (err) {
          console.error("Error deleting rule:", err.message);
      }
  };

  // --- TEMPLATES HANDLERS ---
  const handleSaveTemplate = async () => {
      if (!templateForm.name || !templateForm.content) return alert("Template Name and Content are required.");
      
      try {
          // Insert into Supabase
          const { data, error } = await supabase
              .from('community_message_templates')
              .insert([templateForm])
              .select(); // Select returns the new row with its generated ID

          if (error) throw error;

          // Update UI
          if (data) setTemplates([data[0], ...templates]);
          
          // Close and reset form
          setIsTemplateModalOpen(false);
          setTemplateForm({ name: '', content: '', type: 'Market Update' });
          
      } catch (error) {
          console.error("Supabase Error:", error.message);
          alert("Failed to save template.");
      }
  };

  const handleDeleteTemplate = async (id) => {
      if(!window.confirm("Are you sure you want to delete this template? Any automations using it will stop sending text.")) return;
      try {
          const { error } = await supabase.from('community_message_templates').delete().eq('id', id);
          if (error) throw error;
          setTemplates(templates.filter(t => t.id !== id));
      } catch (err) {
          console.error("Error deleting template:", err.message);
      }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-8 shadow-xl animate-fade-in mt-8">
      
      {/* Header */}
      <div className="border-b border-slate-800 pb-4 mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Zap className="text-amber-500" /> Automation Engine
          </h2>
          <p className="text-sm text-slate-400 mt-1">Manage event-driven templates and scheduled Ghostwriter blasts.</p>
        </div>
        <div className="flex gap-2">
            <button 
                onClick={() => setActiveSubTab('rules')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeSubTab === 'rules' ? 'bg-slate-800 text-white border border-slate-700' : 'text-slate-500 hover:text-slate-300'}`}
            >
                Active Rules
            </button>
            <button 
                onClick={() => setActiveSubTab('templates')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${activeSubTab === 'templates' ? 'bg-slate-800 text-white border border-slate-700' : 'text-slate-500 hover:text-slate-300'}`}
            >
                Template Builder
            </button>
        </div>
      </div>

      {/* --- TEMPLATE BUILDER VIEW (NOW LIVE) --- */}
      {activeSubTab === 'templates' && (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white flex items-center gap-2"><FileText size={18} className="text-blue-400"/> Message Templates</h3>
                <button 
                    onClick={() => setIsTemplateModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors"
                >
                    <Plus size={16}/> New Template
                </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isTemplatesLoading ? (
                    <div className="col-span-full p-8 text-center text-slate-500">Loading templates...</div>
                ) : templates.length === 0 ? (
                    <div className="col-span-full p-8 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl bg-slate-900/50">No templates created yet. Click "New Template" to start.</div>
                ) : (
                    templates.map(template => (
                        <div key={template.id} className="bg-slate-950 border border-slate-800 rounded-xl p-5 relative group hover:border-blue-500/50 transition-colors flex flex-col">
                            <div className="flex justify-between items-start mb-3">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400 bg-blue-500/10 px-2 py-1 rounded border border-blue-500/20">{template.type}</span>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleDeleteTemplate(template.id)} className="text-slate-500 hover:text-red-500"><Trash2 size={14}/></button>
                                </div>
                            </div>
                            <h4 className="font-bold text-white mb-2">{template.name}</h4>
                            <div className="bg-slate-900 border border-slate-800 p-3 rounded-lg flex-1">
                                <p className="text-xs text-slate-400 whitespace-pre-wrap font-mono">{template.content}</p>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
      )}

      {/* --- AUTOMATION RULES VIEW --- */}
      {activeSubTab === 'rules' && (
        <div className="space-y-6 animate-fade-in">
             <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-white flex items-center gap-2"><Settings size={18} className="text-emerald-400"/> Trigger Rules</h3>
                <button className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors opacity-50 cursor-not-allowed" title="Rule creation is managed by backend developers">
                    <Plus size={16}/> New Rule
                </button>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-900 border-b border-slate-800 text-slate-400 text-xs uppercase">
                        <tr>
                            <th className="p-4 font-bold w-1/3">Automation Name</th>
                            <th className="p-4 font-bold w-1/4">Trigger / Schedule</th>
                            <th className="p-4 font-bold w-1/4">Template Attached</th>
                            <th className="p-4 font-bold text-right w-32">Status</th>
                            <th className="p-4 font-bold text-right"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 text-slate-300">
                        {isRulesLoading ? (
                            <tr><td colSpan="5" className="p-8 text-center text-slate-500">Loading rules...</td></tr>
                        ) : rules.length === 0 ? (
                            <tr><td colSpan="5" className="p-8 text-center text-slate-500">No automation rules configured yet.</td></tr>
                        ) : (
                            rules.map(rule => (
                                <tr key={rule.id} className="hover:bg-slate-900/50 transition-colors group">
                                    <td className="p-4 font-bold text-white">{rule.name}</td>
                                    <td className="p-4">
                                        <span className="flex items-center gap-2 text-amber-400">
                                            {rule.trigger.includes('Daily') || rule.trigger.includes('06:00') ? <Clock size={14}/> : <Zap size={14}/>} 
                                            {rule.trigger}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <span className="text-xs font-mono bg-slate-900 rounded p-1.5 border border-slate-800 text-slate-400">
                                            {rule.template || 'No Template'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button 
                                            onClick={() => toggleRuleStatus(rule.id, rule.active)}
                                            className={`flex items-center justify-end gap-2 w-full transition-colors ${rule.active ? 'text-emerald-500 hover:text-emerald-400' : 'text-slate-500 hover:text-slate-400'}`}
                                        >
                                            <span className="text-[10px] font-bold uppercase tracking-wider">{rule.active ? 'Active' : 'Paused'}</span>
                                            {rule.active ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                                        </button>
                                    </td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => deleteRule(rule.id)} className="text-slate-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                                            <Trash2 size={16}/>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      )}

      {/* --- TEMPLATE CREATION MODAL --- */}
      {isTemplateModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
              <div className="bg-slate-900 border border-slate-700 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col">
                  
                  {/* Modal Header */}
                  <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-950/50">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2"><FileText className="text-blue-400" size={18}/> Create New Template</h3>
                      <button onClick={() => setIsTemplateModalOpen(false)} className="text-slate-500 hover:text-white transition-colors"><X size={20}/></button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 space-y-5">
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Template Name</label>
                          <input 
                              type="text" 
                              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors" 
                              placeholder="e.g. End of Week Summary"
                              value={templateForm.name}
                              onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                          />
                      </div>
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Category Type</label>
                          <select 
                              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors"
                              value={templateForm.type}
                              onChange={(e) => setTemplateForm({...templateForm, type: e.target.value})}
                          >
                              <option value="Market Update">Market Update</option>
                              <option value="Onboarding">Onboarding</option>
                              <option value="Alert">Emergency Alert</option>
                              <option value="Promotion">Promotion</option>
                          </select>
                      </div>
                      <div>
                          <label className="text-xs font-bold text-slate-400 uppercase mb-2 block flex justify-between">
                              Message Content
                              <span className="text-slate-600 font-normal lowercase">Use {"{Var}"} for dynamic data</span>
                          </label>
                          <textarea 
                              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white h-40 resize-none font-mono focus:outline-none focus:border-blue-500 transition-colors" 
                              placeholder="Type your message template here..."
                              value={templateForm.content}
                              onChange={(e) => setTemplateForm({...templateForm, content: e.target.value})}
                          ></textarea>
                      </div>
                  </div>

                  {/* Modal Footer */}
                  <div className="p-5 border-t border-slate-800 bg-slate-950/50 flex justify-end gap-3">
                      <button onClick={() => setIsTemplateModalOpen(false)} className="px-5 py-2.5 rounded-lg text-sm font-bold text-slate-300 hover:bg-slate-800 transition-colors">Cancel</button>
                      <button onClick={handleSaveTemplate} className="px-6 py-2.5 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-500 transition-colors flex items-center gap-2"><Save size={16}/> Save Template</button>
                  </div>

              </div>
          </div>
      )}

    </div>
  );
};

export default AutomationsTab;